import * as React from "react";
import { FindPlaceCandidate } from "../../apis/google";
import { SystemServiceCode, useAuthStore } from "../../stores";
import { useApi, useNavigation, useSearchParams } from "../../hooks";
import { useTranslation } from "react-i18next";
import type { TableColumn } from "../../components/Table";
import { getDashboardColumns } from "./columns";
import { APIs } from "../../apis";
import type { Product, ProductsResponse } from "../../apis/products";
import { NearbyPlaceType, ProductsParamsSortBy } from "../../apis/products";
import { addDays, format } from "date-fns";
import { DatePickerRangeValue } from "../../components";
import { SVGs } from "../../assets";
import { Tools } from "../../utils";

const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_PAGE = 1;

export const nearbyPlaceOptions = {
  [NearbyPlaceType.parkingLot]: {
    icon: SVGs.ParkingLot,
  },
  [NearbyPlaceType.busStation]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.busStop]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.transitStation]: {
    icon: SVGs.Bus,
  },
  [NearbyPlaceType.trainStation]: {
    icon: SVGs.Train,
  },
};

// Legacy mock data removed (dataSource comes from API now).

export function useViewModel() {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { fetch, data, loading } = useApi<ProductsResponse>();
  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const { results: dataSource = [], count: totalItems = 0 } =
    data?.data?.partnerProducts ?? {};
  const navigateToBooking = React.useCallback(
    (data: Product) => navigate(`/booking`, { data }),
    [navigate],
  );
  const defaultDateRange = React.useMemo(() => {
    const today = new Date();
    return {
      from: format(today, "yyyy-MM-dd"),
      to: format(addDays(today, 3), "yyyy-MM-dd"),
    };
  }, []);
  const { searchParams, setSearchParams } = useSearchParams({
    placeName:
      user?.location?.formattedAddress ??
      user?.location?.displayName?.text ??
      "",
    latitude: user?.location?.location?.latitude?.toString() ?? "",
    longitude: user?.location?.location?.longitude?.toString() ?? "",
    countryCode: Tools.getCountryCodeFromPlace(user?.location),
    sortBy: ProductsParamsSortBy.availability,
    serviceTypeId: (user?.systemServices ?? [])[0]?.id?.toString() ?? "",
    language: (user?.systemLanguages ?? [])[0]?.id?.toString() ?? "",
    dateFrom: defaultDateRange.from,
    dateTo: defaultDateRange.to,
  });

  const coords = React.useMemo(() => {
    if (!searchParams.latitude || !searchParams.longitude) return undefined;
    return {
      latitude: Number(searchParams.latitude),
      longitude: Number(searchParams.longitude),
    };
  }, [searchParams.latitude, searchParams.longitude]);

  const service = React.useMemo(
    () =>
      (user?.systemServices ?? []).find(
        (s) => s.id?.toString() === searchParams.serviceTypeId,
      ),
    [searchParams.serviceTypeId, user?.systemServices],
  );
  const columns = React.useMemo(
    () =>
      getDashboardColumns({
        t,
        navigateToBooking,
        availableServices: user?.availableServices,
      }),
    [navigateToBooking, t, user?.availableServices],
  );

  const getData = React.useCallback(async () => {
    if (!coords) return;
    try {
      const res = await fetch(async (prevRes) => {
        const res = await APIs.products.products({
          page,
          pageSize,
          serviceTypeId: searchParams.serviceTypeId,
          dateFrom: searchParams.dateFrom,
          dateTo: searchParams.dateTo,
          ...coords,
          sortBy: searchParams.sortBy,
        });
        const { results = [] } = res.data.partnerProducts;
        res.data.partnerProducts.results =
          pageSize === DEFAULT_PAGE_SIZE && page > DEFAULT_PAGE
            ? [...(prevRes?.data?.partnerProducts?.results ?? []), ...results]
            : [...results];
        return res;
      });
      if (!res) return;
    } catch {}
  }, [
    fetch,
    page,
    pageSize,
    searchParams.dateFrom,
    searchParams.dateTo,
    searchParams.serviceTypeId,
    searchParams.sortBy,
    coords,
  ]);

  React.useEffect(() => {
    void getData();
  }, [getData]);

  const setFilter = React.useCallback(
    (name: string, value: string | DatePickerRangeValue) => {
      setPage(DEFAULT_PAGE);
      setPageSize(
        name === "sortBy"
          ? Math.max(dataSource.length, DEFAULT_PAGE_SIZE)
          : DEFAULT_PAGE_SIZE,
      );
      if (name === "sortBy") {
        setSearchParams({ sortBy: value as string });
        return;
      }
      if (name === "dateRange" && typeof value === "object") {
        setSearchParams({
          sortBy: ProductsParamsSortBy.availability,
          dateFrom: value.from,
          dateTo: value.to,
        });
        return;
      }
      setSearchParams({
        sortBy: ProductsParamsSortBy.availability,
        [name]: value as string,
      });
    },
    [dataSource.length, setSearchParams],
  );

  const onLoadMore = React.useCallback(() => {
    if (loading) return;
    setPageSize(DEFAULT_PAGE_SIZE);
    setPage(Math.floor(dataSource.length / DEFAULT_PAGE_SIZE) + 1);
  }, [loading, dataSource.length]);

  return {
    ...searchParams,
    coords,
    dateRange: {
      from: searchParams.dateFrom,
      to: searchParams.dateTo,
    },
    page: 1,
    pageSize: dataSource.length,
    t,
    columns,
    dataSource,
    service,
    navigateToBooking,
    totalItems,
    loading,
    serviceOptions: (user?.systemServices ?? []).map((s) => ({
      value: s.id.toString(),
      label: s.name,
    })),
    serviceLanguageOptions: (user?.systemLanguages ?? []).map((s) => ({
      value: s.id.toString(),
      label: s.name,
    })),
    onLoadMore,
    setFilter,
    onSelectPlace: (p: FindPlaceCandidate) => {
      setPage(1);
      setSearchParams({
        placeName: p.formattedAddress ?? p.displayName?.text ?? "",
        latitude: p.location?.latitude?.toString() ?? "",
        longitude: p.location?.longitude?.toString() ?? "",
        countryCode: Tools.getCountryCodeFromPlace(p),
      });
    },
  };
}

export type DashboardCoords = { latitude: number; longitude: number };

export type DashboardViewModel = {
  // filters
  serviceTypeId: string;
  service?: { code?: SystemServiceCode | null; name?: string };
  placeName: string;
  latitude: string;
  longitude: string;
  coords?: DashboardCoords;
  countryCode: string;
  language: string;
  dateFrom: string;
  dateTo: string;
  dateRange: DatePickerRangeValue;
  sortBy: string;
  setFilter: (
    name: "serviceTypeId" | "language" | "dateRange" | "sortBy",
    value: string | DatePickerRangeValue,
  ) => void;
  serviceOptions: Array<{ value: string; label: string }>;
  serviceLanguageOptions: Array<{
    value: string;
    label: string;
  }>;

  // places
  onSelectPlace: (p: FindPlaceCandidate) => void;

  // table
  t: (key: string) => string;
  navigateToBooking: (data: Product) => void;
  columns: TableColumn<Product>[];
  dataSource: Product[];
  totalItems: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onLoadMore: () => void;
};
